import { useEffect, useState } from "react";
import PropTypes from "prop-types";

import CircularProgress from "@mui/material/CircularProgress";
import DownloadIcon from "@mui/icons-material/Download";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import SearchableSelect from "components/SearchableSelect";
import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import SoftPagination from "components/SoftPagination";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import FlagLoader from "layouts/flagloader";

const filterFieldStyle = {
  flex: "1 1 150px",
  minWidth: "150px",
};

const filterLabelStyle = {
  fontSize: "0.8125rem",
};

const sidebarLabelStyle = {
  fontSize: "0.8125rem",
};

const headerCellStyle = {
  backgroundColor: "#5c91c5",
  color: "#fff",
  fontSize: "0.75rem",
  fontWeight: 600,
  lineHeight: 1.2,
  padding: "9px 8px",
  textAlign: "center",
  whiteSpace: "nowrap",
};

const bodyCellStyle = {
  fontSize: "0.8125rem",
  lineHeight: 1.25,
  padding: "8px",
  textAlign: "center",
  whiteSpace: "nowrap",
};

const selectProps = {
  className: "basic-select",
  classNamePrefix: "select",
  fontSize: "0.875rem",
  isMulti: true,
  isSearchable: true,
};

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const summaryValue = (value) => Number(value || 0).toLocaleString();

const exactIdFilter = (value) => {
  const normalized = String(value || "").trim();

  return normalized ? [{ value: normalized, label: normalized }] : [];
};

function FilterField({ label, children }) {
  return (
    <div style={filterFieldStyle}>
      <Typography variant="body2" gutterBottom style={filterLabelStyle}>
        {label}
      </Typography>
      {children}
    </div>
  );
}

FilterField.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

function StudiesSurveyResponsesLayout({ model }) {
  const [ridExactInput, setRidExactInput] = useState("");
  const [pidExactInput, setPidExactInput] = useState("");
  const {
    applyFilters,
    branchFilter,
    canExportSurveyResponses,
    canSearchResponses,
    canViewClient,
    canViewCompletesSummary,
    canViewCountry,
    canViewIpMismatchSummary,
    canViewProjectCode,
    canViewResponseFilter,
    canViewRevenueSummary,
    canViewStatus,
    clientFilter,
    clearSelectValue,
    countryFilter,
    endDateFilter,
    exportLoading,
    exportResponses,
    goToResponsePage,
    handleBranchFilterChange,
    handleShiftFilterChange,
    handleSubBranchFilterChange,
    handleVendorFilterChange,
    hasSingleVendorBranch,
    loading,
    pageInput,
    pagination,
    paginationEnd,
    paginationStart,
    paginationTotal,
    projectCodeFilter,
    responseColumns,
    responseSummary,
    searchInput,
    setClientFilter,
    setCountryFilter,
    setEndDateFilter,
    setPageInput,
    setPidFilter,
    setProjectCodeFilter,
    setRidFilter,
    setSearch,
    setSearchInput,
    setStartDateFilter,
    setStatus,
    setUserFilter,
    shiftFilter,
    showUserFilter,
    showVendorFilter,
    startDateFilter,
    status,
    statusOptions,
    subBranchFilter,
    userFilter,
    vendorBranchOptions,
    vendorClientOptions,
    vendorCountryOptions,
    vendorFilter,
    vendorProjectCodeOptions,
    vendorSubBranchOptions,
    vendorUserOptions,
    vendorVendorOptions,
    vendorShiftOptions,
    visibleResponses,
  } = model;

  useEffect(() => {
    if (!canSearchResponses) return undefined;

    const timeoutId = window.setTimeout(() => {
      setSearch(searchInput.trim());
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [canSearchResponses, searchInput, setSearch]);

  const updateSelect = (setter) => (option, actionMeta) => {
    setter(clearSelectValue(option, actionMeta));
  };

  const renderResponseCell = (row, column) => {
    const value = column.render(row);
    return (
      <TableCell
        key={`${row.id}-${column.key}`}
        style={{
          ...bodyCellStyle,
          maxWidth: column.truncate ? "220px" : "none",
          overflow: column.truncate ? "hidden" : "visible",
          textOverflow: column.truncate ? "ellipsis" : "clip",
        }}
        title={column.title ? column.title(row) : String(displayValue(value))}
      >
        {displayValue(value)}
      </TableCell>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div style={{ display: "flex", alignItems: "stretch", gap: "10px" }}>
        <Card style={{ flex: 1, minWidth: 0, borderRadius: "12px", padding: "10px" }}>
          <div
            style={{
              display: "flex",
              gap: "5px",
              marginBottom: "20px",
              flexWrap: "wrap",
            }}
          >
            {canViewResponseFilter("branch") && (
              <FilterField label="Branch">
                <SearchableSelect
                  {...selectProps}
                  name="branch"
                  options={vendorBranchOptions}
                  value={branchFilter}
                  onChange={handleBranchFilterChange}
                  isClearable={!hasSingleVendorBranch}
                />
              </FilterField>
            )}

            {canViewResponseFilter("sub_branch") && (
              <FilterField label="Sub Branch">
                <SearchableSelect
                  {...selectProps}
                  name="sub_branch"
                  options={vendorSubBranchOptions}
                  value={subBranchFilter}
                  onChange={handleSubBranchFilterChange}
                  isClearable
                />
              </FilterField>
            )}

            {canViewResponseFilter("shift") && (
              <FilterField label="Shift">
                <SearchableSelect
                  {...selectProps}
                  name="shift"
                  options={vendorShiftOptions}
                  value={shiftFilter}
                  onChange={handleShiftFilterChange}
                  isClearable
                />
              </FilterField>
            )}
          </div>

          <div
            style={{
              display: "flex",
              gap: "5px",
              marginBottom: "10px",
              flexWrap: "wrap",
            }}
          >
            {showUserFilter && (
              <FilterField label="Users">
                <SearchableSelect
                  {...selectProps}
                  name="user"
                  options={vendorUserOptions}
                  value={userFilter}
                  onChange={updateSelect(setUserFilter)}
                  isClearable
                />
              </FilterField>
            )}

            {canViewProjectCode && canViewResponseFilter("project_code") && (
              <FilterField label="Project Code">
                <SearchableSelect
                  {...selectProps}
                  name="project_code"
                  options={vendorProjectCodeOptions}
                  value={projectCodeFilter}
                  onChange={updateSelect(setProjectCodeFilter)}
                  isClearable
                />
              </FilterField>
            )}

            {canViewCountry && canViewResponseFilter("country") && (
              <FilterField label="Country">
                <SearchableSelect
                  {...selectProps}
                  name="country"
                  options={vendorCountryOptions}
                  value={countryFilter}
                  onChange={updateSelect(setCountryFilter)}
                  isClearable
                />
              </FilterField>
            )}

            {canViewClient && canViewResponseFilter("client") && (
              <FilterField label="Client">
                <SearchableSelect
                  {...selectProps}
                  name="client"
                  options={vendorClientOptions}
                  value={clientFilter}
                  onChange={updateSelect(setClientFilter)}
                  isClearable
                />
              </FilterField>
            )}

            {canViewStatus && canViewResponseFilter("status") && (
              <FilterField label="Status">
                <SearchableSelect
                  {...selectProps}
                  name="status"
                  options={statusOptions}
                  value={status}
                  onChange={(options) => setStatus(options || [])}
                  isClearable
                />
              </FilterField>
            )}
          </div>

          <div style={{ overflowX: "auto" }}>
            {loading ? (
              <SoftBox display="flex" justifyContent="center" alignItems="center" height="300px">
                <FlagLoader />
              </SoftBox>
            ) : (
              <>
                <SoftBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  p={1}
                  style={{ width: "100%", fontSize: "0.875rem" }}
                >
                  <SoftBox fontWeight="small" style={{ width: "100%" }}>
                    Showing {paginationStart} to {paginationEnd} of {paginationTotal} entries
                  </SoftBox>

                  <SoftBox
                    display="flex"
                    justifyContent="end"
                    alignItems="center"
                    p={1}
                    style={{ width: "100%" }}
                  >
                    <SoftPagination
                      item
                      onClick={() => goToResponsePage(1)}
                      disabled={pagination.current_page <= 1}
                    >
                      <Icon sx={{ fontWeight: "bold" }}>first_page</Icon>
                    </SoftPagination>
                    <SoftPagination
                      item
                      onClick={() => goToResponsePage(pagination.current_page - 1)}
                      disabled={pagination.current_page <= 1}
                    >
                      <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                    </SoftPagination>
                    <span
                      style={{
                        margin: "0 5px",
                        width: "120px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ width: "5rem", fontSize: "0.8125rem" }}>
                        <SoftInput
                          inputProps={{
                            type: "number",
                            min: 1,
                            max: pagination.last_page || 1,
                          }}
                          value={pageInput}
                          onChange={(event) =>
                            setPageInput(event.target.value.replace(/\D/g, ""))
                          }
                          onBlur={() => goToResponsePage(pageInput)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") goToResponsePage(pageInput);
                          }}
                        />
                      </span>
                      / <span>{pagination.last_page || 1}</span>
                    </span>
                    <SoftPagination
                      item
                      onClick={() => goToResponsePage(pagination.current_page + 1)}
                      disabled={pagination.current_page >= pagination.last_page}
                    >
                      <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                    </SoftPagination>
                    <SoftPagination
                      item
                      onClick={() => goToResponsePage(pagination.last_page)}
                      disabled={pagination.current_page >= pagination.last_page}
                    >
                      <Icon sx={{ fontWeight: "bold" }}>last_page</Icon>
                    </SoftPagination>
                  </SoftBox>
                </SoftBox>

                {(canViewRevenueSummary ||
                  canViewCompletesSummary ||
                  canViewIpMismatchSummary) && (
                  <SoftBox style={{ width: "100%", fontSize: "0.875rem", marginBottom: "6px" }}>
                    {canViewRevenueSummary && (
                      <span>
                        <span style={{ color: "rgb(23, 193, 232)" }}>Revenue:</span>{" "}
                        {summaryValue(responseSummary.total_revenue)}{" "}
                      </span>
                    )}
                    {canViewCompletesSummary && (
                      <span>
                        | <span style={{ color: "rgb(23, 193, 232)" }}>Completes:</span>{" "}
                        {summaryValue(responseSummary.total_completes)}{" "}
                      </span>
                    )}
                    {canViewIpMismatchSummary && (
                      <span>
                        | <span style={{ color: "#dc2626" }}>IP Mismatches:</span>{" "}
                        {summaryValue(responseSummary.total_ip_mismatches)}
                      </span>
                    )}
                  </SoftBox>
                )}

                <Table style={{ width: "100%", minWidth: `${responseColumns.length * 115}px` }}>
                  <TableHead style={{ padding: 0, display: "table-header-group" }}>
                    <TableRow>
                      {responseColumns.map((column) => (
                        <TableCell key={column.key} style={headerCellStyle}>
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleResponses.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={Math.max(responseColumns.length, 1)}
                          style={bodyCellStyle}
                        >
                          No survey responses found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      visibleResponses.map((row, rowIndex) => (
                        <TableRow
                          key={row.id || rowIndex}
                          style={{
                            backgroundColor: row.ip_mismatch ? "#fff3bf" : "transparent",
                            boxShadow: row.ip_mismatch ? "inset 4px 0 0 #f59e0b" : "none",
                          }}
                        >
                          {responseColumns.map((column) => renderResponseCell(row, column))}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </>
            )}
          </div>
        </Card>

        <div style={{ display: "flex", flexDirection: "column", gap: "50px" }}>
          <Card
            style={{
              padding: "8px",
              width: "200px",
              height: "100%",
              borderRadius: "12px",
            }}
          >
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="body2" style={sidebarLabelStyle}>
                  Count of SessionId
                </Typography>
                {canExportSurveyResponses && (
                  <IconButton
                    color="primary"
                    onClick={() => exportResponses("all")}
                    disabled={!visibleResponses.length || exportLoading}
                  >
                    {exportLoading ? <CircularProgress size={24} /> : <DownloadIcon />}
                  </IconButton>
                )}
              </div>
              <Typography
                variant="h4"
                color="primary"
                style={{ fontSize: "20px", textAlign: "center" }}
              >
                {summaryValue(paginationTotal)}
              </Typography>
            </div>

            {canViewResponseFilter("start_date") && (
              <div style={{ marginTop: "12px" }}>
                <Typography variant="body2" style={sidebarLabelStyle}>
                  Start Date
                </Typography>
                <SoftInput
                  type="datetime-local"
                  value={startDateFilter}
                  onChange={(event) => setStartDateFilter(event.target.value)}
                />
              </div>
            )}

            {canViewResponseFilter("end_date") && (
              <div style={{ marginTop: "12px" }}>
                <Typography variant="body2" style={sidebarLabelStyle}>
                  End Date
                </Typography>
                <SoftInput
                  type="datetime-local"
                  value={endDateFilter}
                  onChange={(event) => setEndDateFilter(event.target.value)}
                />
              </div>
            )}

            {canSearchResponses && (
              <div style={{ marginTop: "12px" }}>
                <Typography variant="body2" style={sidebarLabelStyle}>
                  Type here
                </Typography>
                <SoftInput
                  type="text"
                  value={searchInput}
                  placeholder="RID, PID, vendor, user..."
                  onChange={(event) => setSearchInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") applyFilters();
                  }}
                />
              </div>
            )}

            {canViewResponseFilter("rid") && (
              <div style={{ marginTop: "12px" }}>
                <Typography variant="body2" style={sidebarLabelStyle}>
                  RID
                </Typography>
                <SoftInput
                  type="text"
                  value={ridExactInput}
                  placeholder="Paste exact RID"
                  onChange={(event) => {
                    const rid = event.target.value;
                    setRidExactInput(rid);
                    if (!rid.trim()) setRidFilter([]);
                  }}
                  onPaste={(event) => {
                    event.preventDefault();
                    const rid = event.clipboardData.getData("text").trim();
                    setRidExactInput(rid);
                    setRidFilter(exactIdFilter(rid));
                  }}
                />
              </div>
            )}

            {canViewResponseFilter("pid") && (
              <div style={{ marginTop: "12px" }}>
                <Typography variant="body2" style={sidebarLabelStyle}>
                  PID
                </Typography>
                <SoftInput
                  type="text"
                  value={pidExactInput}
                  placeholder="Paste exact PID"
                  onChange={(event) => {
                    const pid = event.target.value;
                    setPidExactInput(pid);
                    if (!pid.trim()) setPidFilter([]);
                  }}
                  onPaste={(event) => {
                    event.preventDefault();
                    const pid = event.clipboardData.getData("text").trim();
                    setPidExactInput(pid);
                    setPidFilter(exactIdFilter(pid));
                  }}
                />
              </div>
            )}

            {showVendorFilter && (
              <div style={{ marginTop: "12px" }}>
                <Typography variant="body2" style={sidebarLabelStyle}>
                  Vendor
                </Typography>
                <SearchableSelect
                  {...selectProps}
                  name="vendor"
                  options={vendorVendorOptions}
                  value={vendorFilter}
                  onChange={handleVendorFilterChange}
                  isClearable
                />
              </div>
            )}
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}

StudiesSurveyResponsesLayout.propTypes = {
  model: PropTypes.shape({
    applyFilters: PropTypes.func.isRequired,
    branchFilter: PropTypes.arrayOf(PropTypes.object),
    canExportSurveyResponses: PropTypes.bool.isRequired,
    canSearchResponses: PropTypes.bool.isRequired,
    canViewClient: PropTypes.bool.isRequired,
    canViewCompletesSummary: PropTypes.bool.isRequired,
    canViewCountry: PropTypes.bool.isRequired,
    canViewIpMismatchSummary: PropTypes.bool.isRequired,
    canViewProjectCode: PropTypes.bool.isRequired,
    canViewResponseFilter: PropTypes.func.isRequired,
    canViewRevenueSummary: PropTypes.bool.isRequired,
    canViewStatus: PropTypes.bool.isRequired,
    clientFilter: PropTypes.arrayOf(PropTypes.object),
    clearSelectValue: PropTypes.func.isRequired,
    countryFilter: PropTypes.arrayOf(PropTypes.object),
    endDateFilter: PropTypes.string.isRequired,
    exportLoading: PropTypes.bool.isRequired,
    exportResponses: PropTypes.func.isRequired,
    goToResponsePage: PropTypes.func.isRequired,
    handleBranchFilterChange: PropTypes.func.isRequired,
    handleShiftFilterChange: PropTypes.func.isRequired,
    handleSubBranchFilterChange: PropTypes.func.isRequired,
    handleVendorFilterChange: PropTypes.func.isRequired,
    hasSingleVendorBranch: PropTypes.bool.isRequired,
    loading: PropTypes.bool.isRequired,
    pageInput: PropTypes.string.isRequired,
    pagination: PropTypes.shape({
      current_page: PropTypes.number.isRequired,
      last_page: PropTypes.number.isRequired,
      total: PropTypes.number.isRequired,
    }).isRequired,
    paginationEnd: PropTypes.number.isRequired,
    paginationStart: PropTypes.number.isRequired,
    paginationTotal: PropTypes.number.isRequired,
    projectCodeFilter: PropTypes.arrayOf(PropTypes.object),
    responseColumns: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        render: PropTypes.func.isRequired,
        title: PropTypes.func,
        truncate: PropTypes.bool,
      })
    ).isRequired,
    responseSummary: PropTypes.shape({
      total_revenue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      total_completes: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      total_ip_mismatches: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }).isRequired,
    searchInput: PropTypes.string.isRequired,
    setClientFilter: PropTypes.func.isRequired,
    setCountryFilter: PropTypes.func.isRequired,
    setEndDateFilter: PropTypes.func.isRequired,
    setPageInput: PropTypes.func.isRequired,
    setPidFilter: PropTypes.func.isRequired,
    setProjectCodeFilter: PropTypes.func.isRequired,
    setRidFilter: PropTypes.func.isRequired,
    setSearch: PropTypes.func.isRequired,
    setSearchInput: PropTypes.func.isRequired,
    setStartDateFilter: PropTypes.func.isRequired,
    setStatus: PropTypes.func.isRequired,
    setUserFilter: PropTypes.func.isRequired,
    shiftFilter: PropTypes.arrayOf(PropTypes.object),
    showUserFilter: PropTypes.bool.isRequired,
    showVendorFilter: PropTypes.bool.isRequired,
    startDateFilter: PropTypes.string.isRequired,
    status: PropTypes.arrayOf(PropTypes.object).isRequired,
    statusOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
    subBranchFilter: PropTypes.arrayOf(PropTypes.object),
    userFilter: PropTypes.arrayOf(PropTypes.object),
    vendorBranchOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
    vendorClientOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
    vendorCountryOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
    vendorFilter: PropTypes.arrayOf(PropTypes.object),
    vendorProjectCodeOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
    vendorShiftOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
    vendorSubBranchOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
    vendorUserOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
    vendorVendorOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
    visibleResponses: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
};

export default StudiesSurveyResponsesLayout;
